import { TEthinities, TLanguage } from '../../components/FindTalentsContainer/filterEnums';
import { ILike } from '../../redux/account/types';
import { IAttendance } from '../../redux/attendance/types';
import { ICompany } from '../../redux/company/types';
import { IAwardCategory, IAwardPrize, IBookmark } from '../../redux/contest/types';
import { IHost, IPartner } from '../../redux/discovery/types';
import { AwardCategory, AwardStatus } from '../../redux/festival/types';
import { IScoreCategory } from '../../redux/jury/types';
import { IContestRequest } from '../../redux/requests/types';
import { ITalentData } from '../../redux/talent/types';
import { IUserProfile } from '../../redux/user/actions';
import { Follower } from '../../redux/user/model';
import { IWorkProfileSummary } from '../../redux/user/types';
import { IView } from '../../redux/view/types';
import { ActData } from './Cards.types';
import type { GlobalConfigurationContentScrubResponse } from './contentScrub.types';
import { IApplication, User } from './ListRender.types';
import { FormOption, IRegistrationForm } from './forms.types';
import { IContestStatus, Status } from './generic.types';
import { Credit } from './manageProject.types';
import { IAdvertisement, IProjectSummary, IVPaid } from './media.types';
import { ROUTE_NAMES } from './routes.types';
import { IMainSubscriptionPlan } from './subscription.types';
import type { IBuzzbridgeLoyaltyConfig } from './buzzbridgeLoyalty.types';
import type {
    ICampaignDeadlinesConfig,
    ICampaignPlatformConfig,
    ICreatorPayoutFeeConfig,
    IInfluencerTierConfig,
    IUgcBudgetConfig,
} from './ugcCampaignConfig.types';

export type { ICampaignDeadlinesConfig } from './ugcCampaignConfig.types';

export type CurrentProfileType = 'casting_director' | 'talent';

export enum CurrentProfileENUM {
    CASTING_DIRECTOR = 'casting_director',
    TALENT = 'talent',
}
export interface CurrentProfile {
    current_profile: CurrentProfileType;
}

export interface IGetDetails {
    /** The id of the document to be fetched */
    id: string;
}
export interface IUserSummary {
    role: string;
    audme_profile: string;
    _id: string;
    firstName: string;
    lastName: string;
    profile_picture: string;
    id: string;
    userName: string;
    verified?: boolean;
    feature_image?: string;
    visible?: boolean;
    user_type?: string[];
    permissions?: string[];
    feature_image_hash?: string;
    profile_picture_hash?: string;
    gender?: string;
    talent_saved?: boolean;
    profiles?: IWorkProfileSummary[];
    shown_people_of_interest?: boolean;
    email?: string;
}

export enum SoundCategory {
    SOUND_TRACK = 'sound_track',
    SOUND_EFFECT = 'sound_effect',
}

export type SoundCategoryType = 'sound_track' | 'sound_effect';
export interface ISound {
    sound_category: SoundCategoryType;
    created_at: string;
    _id: string;
    title: string;
    sound: string;
    thumbnail: string;
    created_by: IUserSummary;
    createdAt: string;
    updatedAt: string;
    id: string;
    artist: string;
    tag: string;
    status: Status;
    sound_metadata: any;
    duration: number;
    additional_details: any;
}

type Details = {
    stage_name: string;
    user: string;
};

export type Location = {
    type?: string;
    coordinates: Array<number>;
    address: string;
    description?: string;
    short_name?: string;
};

export type ProductionCompany = {
    id: string;
    location?: Location;
    production_type?: Array<string>;
    gallery?: Array<string>;
    demo_reel?: Array<string>;
    company_name?: string;
    number_of_reviews?: number;
    rating?: number;
    about?: string;
    profile_picture?: string;
    productions?: Array<IAudition>;
    followers?: Array<any>;
    number_of_jobs?: number;
    status?: Status;
};

export type CastingDirector = ProductionCompany;

export type IAudition = {
    id: string;
    _id: string;
    title: string;
    imageURI: string;
    location: ILocation;
    production_company: ICompany;
    synopsis: string;
    plot?: string;
    total_submissions: number;
    applied: boolean;
    paid: boolean;
    timeToExpiration: number;
    audition_type: string;
    expiration: string;
    applicants: Array<IApplication>;
    applications: Array<IApplication>;
    created: string;
    attachment: string;
    timezone?: string;
    attachment_type: string;
    attachment_description: string;
    rehearsal_date: string;
    rehearsal_date_unformatted: string;
    tags: Array<string>;
    production_personel: string;
    roles: Array<IRole>;
    interest: number;
    status: Status;
    shoot_location: Location;
    reward: number;
    min_reward?: number;
    max_reward?: number;
    currency?: string;
    reward_frequency: string;
    employment_type: string;
    min_age: string;
    max_age: string;
    required_media: Array<string>;
    required_skill: Array<string>;
    allSubmissions: Array<string>;
    time_created: string;
    created_by: IUserSummary;
    imageCover?: string;
    slug: string;
    job_start_date: string;
    job_end_date: string;
    gig_type: 'cast' | 'crew' | 'technical';
    compensation: 'paid' | 'unpaid' | 'tbd';
    is_feature?: boolean;
    talent_location?: Location;
    scheduled_published_date?: string;
    is_background_color?: boolean;
    background_color?: string;
    font_family?: string;
    has_user_applied?: boolean;
    user_application?: string[];
    has_user_bookmarked?: boolean;
    user_bookmark?: string[];
    percentage_match?: number;
    requirements?: string[];
    description?: string;
    company_website?: string;
    casting_expiration?: string;
    work_location?: ILocation;
    /** Not required for the payload */
    prevImageCover?: string;
    createdAt?: string;
    short_url?: string;
    submission_report?:
        | 'daily digest'
        | 'weekly digest'
        | 'monthly digest'
        | 'applications as they come in'
        | 'prerequisite';
    union_affiliation?:
        | 'AEA'
        | ' SAG-AFTRA'
        | 'non-union'
        | 'not applicable'
        | 'ACTRA'
        | 'AEA'
        | 'CAEA'
        | 'Equity'
        | 'MEAA'
        | 'non-union affiliation'
        | 'other'
        | ' UBCP/ACTRA'
        | 'UDA';
    type_of_work?:
        | 'ad campaign'
        | 'short'
        | 'commercial'
        | 'vertical short form'
        | 'episodic'
        | 'student film'
        | 'theatre'
        | 'director’s reel'
        | 'feature'
        | 'print'
        | 'pilot presentation'
        | 'table read'
        | 'live event';

    usage?: [' all media' | 'internet' | 'national broadcast' | 'international' | 'cinema' | 'personal use'];
    compensation_range_text?: string;
};

export interface ISavedAudition {
    id: string;
    type: string;
    model: IAudition;
    created_at: Date;
}

interface Params {
    details: Details;
    audition?: IAudition;
    role?: IRole;
}

export const handleOnMessagePress = (navigation: any, payload: Params) => {
    navigation.navigate(ROUTE_NAMES.ACCOUNT, {
        screen: ROUTE_NAMES.ACCOUNT,
        params: {
            details: { ...payload, stage_name: payload.details.stage_name || 'actor', user: payload.details.user },
            audition: { id: payload?.audition },
            role: payload?.role,
        },
    });
};

export type ChatType =
    | 'chat'
    | 'audition_invite'
    | 'application_invite'
    | 'user_invite'
    | 'media_request'
    | 'video_message'
    | 'picture_message'
    | 'sound_message'
    | 'location_message'
    | 'document_message';
export enum chatType {
    CHAT = 'chat',
    AUDITION_INIVITE = 'audition_invite',
    APPLICATION_INVITE = 'application_invite',
    USER_INVITE = 'user_invite',
    MEDIA_REQUEST = 'media_request',
}

export type ActMode = 'response' | 'reply' | 'demo' | 'share';

export enum ActStatus {
    PUBLISHED = 'published',
    DRAFT = 'draft',
    PUBLIC = 'public',
    CLOSE_FRIENDS = 'close_friends',
    RECRUITERS = 'recruiters',
    PRIVATE = 'private',
}

export interface TalentProfile {
    id: string;
    _id: string;
    location: string;
    photos: string[];
    skills: string[];
    demo_reel: Array<ActData>;
    audio_reel: string[];
    age_to: number;
    age_from: number;
    hair_color: string;
    birth_data: string;
    height: string;
    body_type: string;
    sex: string;
    profile_picture: string;
    age_range: string;
    stage_name: string;
    past_work: Array<any>;
    filmography: Array<any>;
    weight: number;
    about: string;
    rating: number;
    number_of_reviews: number;
    feature_picture: string;
}

export interface TalentSearchFilter {
    location: Location;
    age_from: number;
    age_to: number;
    age_range: string;
    sex: string;
    height: number;
    skills: string;
    lng: number;
    lat: number;
    searchTerm: string;
    language_spoken: string;
    hair_color: string;
    body_type: string;
}

export interface IContestPrize {
    created_at: string;
    _id: string;
    category_title: string;
    sub_category?: 'male' | 'female';
    year_started: string;
    contest: string;
    created_by: string;
    id: string;
    prize: string;
}

export interface IContestWatchList {
    created_at: string;
    id: string;
    model: IContest;
    type: string;
}

export type ProjectType =
    | 'short'
    | 'feature'
    | 'script'
    | 'commercial'
    | 'skit'
    | 'act'
    | 'reels'
    | 'pitch'
    | 'poster'
    | 'picture'
    | 'default'
    | 'monologue'
    | 'advertisement'
    | 'impressions'
    | 'dance'
    | 'poetry'
    | 'make up'
    | 'rap'
    | 'music'
    | 'fashiom'
    | 'campaign_submission';

export type JudgingStatus =
    | 'winner'
    | 'semi-finalist'
    | 'finalist'
    | 'selected'
    | 'submitted'
    | 'second'
    | 'third'
    | 'fourth';

export interface ISubmission {
    judging_status: JudgingStatus;
    _id: string;
    project: IProject;
    contest: IContest;
    created_by: IUserSummary;
    preselected?: boolean;
    createdAt: string;
    updatedAt: string;
    trending_marker: number;
    id: string;
    host?: IHost;
    partner?: IPartner;
    has_user_voted?: boolean;
    jury_score: IScoreCategory;
    score: INewScore[];
    totalCount: number;
    /** These are used in admin dashboard view */
    all_scores?: number[];
    total_average: number;
    submission_picture?: string;
    category?: string;
    jury_notes?: string[];
    awards?: IAwardPrize[];
    testimonial_video?: IProject;
    selection_state?: 'selected' | 'not_selected';
    /** Server-owned: submission fee collected */
    paid?: boolean;
    sale?: string | { _id: string; completed?: boolean };
    payment_required?: boolean;
    payment_due?: boolean;
    chosen_tier?: { label?: string; fee?: number };
}

export interface IDashboardResponse {
    message: string;
    data: IDashboard;
}

export interface IDashboard {
    contest: Contest;
    submission: Submission;
    project: DataProject;
    watch_list: DataWatchList;
}

export interface Contest {
    contest_recommendation: ContestRecommendation[];
    metrics: ContestMetric[];
}

export interface ContestRecommendation {
    _id: string;
    requirements: string[];
    contest_media: string[];
    voting_required: boolean;
    voting_type: string;
    date_start: Date;
    expiration_date: Date;
    contest_phases_enabled: boolean;
    premium: boolean;
    contest_phase: string;
    status: Status;
    title: string;
    description: string;
    created_by: string;
    contest_phase_dates: any[];
    feature_image?: string;
    trending_marker?: number;
    tag: string[];
    __v?: number;
}

export interface ContestMetric {
    _id: string;
    count: number;
    contest: string[];
}

export interface DataProject {
    user_projects: UserProject[];
    metrics: ProjectMetric[];
}

export interface ProjectMetric {
    _id: string;
    count: number;
    project: string[];
}

export interface UserProject {
    _id: string;
    project_type: string;
    project_gallery: any[];
    visible: boolean;
    team: any[];
    status: Status;
    title: string;
    created_by: string;
    project: string;
    feature_image: string;
}

export interface Submission {
    user_submissions: UserSubmission[];
    metrics: ProjectMetric[];
}

export interface UserSubmission {
    _id: string;
    judging_status: string;
    created_by: string;
    project: UserSubmissionProject;
    contest: ContestRecommendation;
}

export interface UserSubmissionProject {
    _id: string;
    project_type: ProjectType;
    project_gallery: string[];
    visible: boolean;
    team: User[];
    status: Status;
    trending_marker: number;
    title: string;
    created_by: string;
    createdAt: Date;
    updatedAt: Date;
    __v: number;
    project: string;
    feature_image: string;
}

export interface DataWatchList {
    watch_list: WatchListElement[];
    metrics: WatchListMetric[];
}

export interface WatchListMetric {
    _id?: null;
    count: number;
}

export interface GenericMetric {
    _id?: any;
    count?: number;
    [key: string]: any;
}

export interface WatchListElement {
    _id: string;
    details: ContestRecommendation;
}

export interface IDashboardModel {
    contest: IContest[];
    project: IProject[];
    submission: ISubmission[];
    watchlist: IContest[];
    metrics: IMetrics;
}

export interface IMetrics {
    contest?: ContestMetric[];
    project?: ProjectMetric[];
    submission?: ProjectMetric[];
    watchlist?: WatchListMetric[];
}

export interface UserSummary {
    _id: string;
    location: Location;
    firstName: string;
    lastName: string;
    userName: string;
    verified: boolean;
    profile_picture?: string;
    user_type?: string[];
    user_follow_interaction?: Follower[];
    user_following?: boolean;
    follows_user?: boolean;
    feature_image_hash?: string;
    profile_picture_hash?: string;
    gender?: 'male' | 'female';
    awards?: IContestAward[];
    talent_saved?: boolean;
    saved_talent?: [ITalentData?]; // Maximum of 1 item
    shown_people_of_interest?: boolean;
    total_followers?: number;
}

/**
 * The below services the initial user signup flow where
 * we have to present to the user people to follow;
 * This is teh expected response from server, matching users
 * interest.
 */
export interface ITopUser extends UserSummary {
    total_audience?: number;
}

export interface IMetaData {
    codec: string;
    duration: number;
    height: number;
    type: string;
    width: number;
}

export interface IProject {
    project_type: ProjectType | string;
    project_format?: 'act' | 'video';
    project_gallery: string[];
    visible: boolean;
    team: IUserSummary[];
    status: Status;
    trending_marker: number;
    total_likes?: number;
    total_anger?: number;
    total_love?: number;
    scheduled_published_date?: string;
    feature_image: string;
    mentions?: User[];
    total_comments?: number;
    view_count?: number;
    last_seen?: string;
    _id: string;
    title: string;
    description: string;
    created_by?: UserSummary;
    createdAt?: string;
    updatedAt?: string;
    project: string;
    comments?: TComment[];
    id: string;
    video_id?: string;
    public_status?: 'unlisted' | 'private' | 'public';
    disable_comments?: boolean;
    language?: String;
    media_type?: 'picture' | 'video' | 'audio' | 'document';
    tag?: string[];
    metadata?: IMetaData;
    views?: IView;
    /** Determines when the project media encoding has completed */
    processing_complete?: boolean;
    /** The GIF thumbnail */
    thumbnail?: string;
    /** Determines if a project can be downloaded */
    downloadable?: boolean;
    resolutions?: TResolutions;
    download_link?: string;
    user_like?: ILike[];
    user_view?: IView;
    /** Informs if a user has liked this specific project */
    is_user_liked?: boolean;
    /** Informs if a user has seen this particluar project */
    has_user_seen?: boolean;
    hash?: string;
    /** HSL string, handles link to the master m3u8 */
    hls?: string;
    submissions?: ISubmission[];
    awards?: IContestAward[];
    vroll?: IAdvertisement[];
    vpaid?: IVPaid[];
    vtt?: string;
    hash_tags?: string[];
    credits?: Credit[];
    short_url?: string;
}

export type TResolutions = {
    '1080p': string;
    '720p': string;
    '480p': string;
    '360p': string;
};

export interface SubmittedContest {
    trending_marker: number;
    judging_status: string;
    _id: string;
    project: TProjectSummary;
    contest: IContest;
    created_by: string;
    createdAt: string;
    updatedAt: string;
    __v: number;
    id: string;
}

export interface Project {
    project_type: string;
    project_gallery: any[];
    visible: boolean;
    team: any[];
    status: Status;
    trending_marker: number;
    total_likes: number;
    total_anger: number;
    total_love: number;
    scheduled_published_date: string;
    mentions: any[];
    total_comments: number;
    view_count: number;
    last_seen: string;
    _id: string;
    title: string;
    description: string;
    created_by: UserSummary;
    createdAt: string;
    updatedAt: string;
    __v: number;
    project: string;
    id: string;
    hash?: string;
}

export type ContestRating = 'G' | 'PG' | 'PG-13' | 'R' | 'NC-17' | 'NR' | 'NR-17';

export type Format = '16:9' | '4:3' | '1:1' | '9:16';

export type MediaFormat = 'video' | 'audio' | 'picture' | 'document';

export type TProjectFormat = 'vertical' | 'horizontal';

export type TPhase = 'submission' | 'ideation' | 'pitch' | 'production' | 'judging' | 'vote' | 'none' | 'event';

export interface PhaseData {
    phase: TPhase;
    date_started: string;
    expiration_date: string;
}

export interface ITechnicalSpec {
    min_duration: number;
    max_duration: number;
    format: Format;
    media_type: MediaFormat;
    project_format?: TProjectFormat;
}

export type TContestGenre =
    | 'education'
    | 'edutainment'
    | 'open air casting'
    | 'acting'
    | 'creative advert'
    | 'tv advertisement'
    | 'expressions'
    | 'music'
    | 'artistic expression'
    | 'film festival'
    | 'music festival';

export type TContestPhase =
    | 'preparation' // Before submissions begin
    | 'submission' // Submit Projects
    | 'ideation' // Submit ideas
    | 'pitch' // Submit Pitch
    | 'production' // Go and produce
    | 'judging'
    | 'vote'
    | 'vote'
    | 'none'
    | 'closed'
    | 'event'
    | 'registration';

export interface IFeeStructure {
    category_name: string;
    category_description: string;
    with_fee: boolean;
    fee_name?: string;
    fee?: number;
    currency?: string;
    fee_features?: string[];
}

export interface TSubmissionCategoryAward {
    // Replace these fields with the actual schema definition of subawardSchema
    additional_prize: string[];
    category_title: string;
    contest: string;
    updatedAt: string;
    sub_category: string;
    /** This value is not in cents */
    prize: number | string;
    converted_amount_no_symbol?: string;
    transaction_currency?: string;
    currency?: string; // The original currency that this was set in
    rate?: number;
    bare_amount?: number; // without any format
    created_by: string;
    original_currency?: string;
    defaulted_to_usd?: boolean;
    formatted_for_display?: string;
    _id: string;
    tier_position?: string; // position in prize structure
    tier_name?: string;
}

export interface FeeObject {
    amount: string;
    bare_amount: number;
    currency: string;
}

export interface ITier {
    label: string;
    fee: string;
    currency: string;
    _id: string;
    with_subscriber_discount?: boolean;
    subscriber_discount?: number;
    is_free_for_subscriber?: boolean;
    bare_amount: number;
    converted_amount_no_symbol: string;
    discount_message: string;
    discounted_price: string;
    original_currency: string;
    original_price: number;
    rate: number;
    transaction_currency: string;
    defaulted_to_usd?: boolean;
    formatted_for_display?: string;
    /** Dated tier window (ISO). Null = open from / until submission phase bounds. */
    starts_at?: string | null;
    ends_at?: string | null;
    timezone?: string | null;
    is_active?: boolean;
    /** Server-computed availability on contest detail. */
    is_open?: boolean;
    opens_in_seconds?: number | null;
    closes_in_seconds?: number | null;
    formatted_window?: string;
}

export interface SubmissionCategory {
    _id?: string;
    category_name: string;
    category_description?: string;
    category_awards?: TSubmissionCategoryAward[];
    with_fee?: boolean;
    fee?: string;
    currency?: string;
    with_subscriber_discount?: boolean;
    subscriber_discount?: number;
    is_free_for_subscriber?: boolean;
    formatted_for_display?: string;
    defaulted_to_usd?: boolean;
    with_tiered_pricing?: boolean;
    tier_fee?: ITier[];
    /** Set after tier → regular convert: which tier became the flat fee. */
    regular_default_tier_id?: string;
    regular_default_tier_label?: string;
    prize_structure?: Array<{
        position: string;
        payouts: IAwardCategory[] | string[]; // Array of AwardCategory IDs
    }>;
    discount_message: string;
    discounted_price?: string;
    rate: number;
    original_price: number;
    converted_amount_no_symbol: string;
    /** use this value for transaction */
    transaction_currency: string;
    bare_amount: number;
    original_currency: string;
    amount: string;
    has_applied?: boolean;
}

export type TContestRefundPolicy = 'non-refundable' | 'refundable during submission';

export type TRequirements = {
    header: string;
    detail: string;
};

/** Aggregated contest prize pool from the API (may be null / omitted). */
export interface IContestPrizePool {
    converted_amount_no_symbol?: string | null;
    transaction_currency?: string | null;
    currency?: string | null;
    rate?: number | null;
    bare_amount?: number | null;
    original_currency?: string | null;
    defaulted_to_usd?: boolean | null;
    formatted_for_display?: string | null;
}

/** Hydrated sample / example video on contest details (`sample_videos.videos[]`). */
export interface IContestSampleVideo {
    id: string;
    title?: string;
    description?: string;
    sort_order?: number;
    active?: boolean;
    project_id?: string;
    thumbnail_url?: string;
    hls_url?: string;
    resolutions?: IProject['resolutions'];
    project?: Partial<IProject> & {
        _id: string;
        title?: string;
        description?: string;
        feature_image?: string;
        thumbnail?: string;
        social_thumbnail?: string;
        hls?: string;
        video_id?: string;
        duration?: number;
        project_format?: string;
        resolutions?: IProject['resolutions'];
    };
}

export interface IContestSampleVideos {
    resolved_country_code?: string;
    fallback_used?: boolean;
    videos: IContestSampleVideo[];
}

export interface IContest {
    __v: number;
    _id: string;
    /** Will return contest_code request, if user has requested */
    amount_available?: number;
    attachments?: string[];
    approved?: boolean;
    awards?: IContestAward[];
    categories_enabled?: boolean;
    categories?: SubmissionCategory[];
    contest_attachment?: string;
    contest_id: string;
    contest_media: any[];
    contest_open_email_sent?: boolean;
    contest_phase_dates: PhaseData[];
    contest_phase: TContestPhase;
    contest_phases_enabled: boolean;
    contest_reference?: string[];
    contest_video?: IProjectSummary;
    created_by: string;
    date_start: string;
    description: string;
    discounts?: any[];
    entry_type?: 'single' | 'multiple';
    /** Max applications / submissions allowed; omit or null for unlimited. */
    max_participants?: number | null;
    /**
     * When true, public payloads omit `applications` and clear playlists.
     * Hosts still see real counts on manage paths. Prefer this plural field name.
     */
    hide_submissions_count?: boolean;
    /** Server: whether a new application is allowed (capacity + submission phase). */
    can_apply?: boolean;
    /** Server-reported submission total when submissions array is not fully loaded. */
    submissions_total_count?: number;
    /** Total applications (used on list/detail payloads; preferred over submissions.length). */
    applications?: number;
    expiration_date: string;
    feature_image: string;
    fee_structure?: IFeeStructure[];
    funded?: boolean;
    genre?: TContestGenre[];
    has_user_bookmarked?: boolean;
    hash?: string;
    host?: IHost;
    id: string;
    incomplete?: boolean | string;
    is_feature: boolean;
    is_subscription_active?: boolean;
    is_user_liked?: boolean;
    kind?: 'online' | 'physical';
    late_entry?: boolean;
    likes?: ILike[];
    mobile_feature_image?: string;
    partner?: IPartner[];
    playlist?: string[];
    playlists: IThspianPlaylist[];
    preferred_name?: string;
    premium: boolean;
    preselection_enabled?: boolean;
    /** Server-aggregated prize total (preferred over summing awards client-side). */
    prize_pool?: IContestPrizePool | null;
    prizes?: IAwardCategory[];
    rating?: ContestRating;
    refund_policy?: TContestRefundPolicy;
    registration_form?: IRegistrationForm[];
    registration_required?: boolean;
    request?: IContestRequest;
    requirements: TRequirements[] | string[];
    sales?: any[];
    score_criteria?: string[];
    /** Country-resolved sample / example videos from platform config (always on detail fetch). */
    sample_videos?: IContestSampleVideos;
    similar_contests?: IContest[];
    slug?: string;
    /** Optional OG / share image path (may be relative S3 key). */
    social_thumbnail?: string;
    status: Status;
    submission_email_sent?: boolean;
    submission_state?: 'private_submission' | 'public_submission';
    submissions?: ISubmission[];
    tag: string[];
    technical_spec?: ITechnicalSpec;
    thumbnail?: string;
    timezone?: string;
    title: string;
    total_anger: number;
    total_comments: number;
    total_likes: number;
    total_love: number;
    trending_marker: number;
    user_bookmark?: IBookmark;
    user_contest_request?: IContestRequest;
    user_has_requested?: boolean;
    user_has_saved?: boolean;
    user_has_submitted?: boolean;
    user_like?: ILike[];
    user_saved_contest?: any[];
    user_submissions?: ISubmission[];
    user_subscription?: IMainSubscriptionPlan;
    vote_email_sent?: boolean;
    voting_required: boolean;
    voting_type: string;
    winner_email_sent?: boolean;
    with_fee?: boolean;
    submission_mode?: 'tier' | 'regular';
}

export interface IComment {
    created_at?: string;
    total_likes?: number;
    total_comments?: number;
    _id?: string;
    comment: string;
    project?: IProject;
    act?: IProject;
    thougth?: IThougth;
    contest?: IContest;
    user?: TUserSummary;
    created_by?: string;
    createdAt?: string;
    updatedAt?: string;
    id?: string;
    total_love?: number;
    total_anger?: number;
    total_dislikes?: number;
    likes?: Array<CommentLike>;
    parent_comment?: string;
    replies?: IComment[];
    user_like?: CommentLike[];
    is_user_liked?: boolean;
}

export type Likes = 'like' | 'dislike' | 'love' | 'angry';
/** New Convention For Typing */
export type TComment = {
    created_at: string;
    total_likes: number;
    total_love?: number;
    total_anger?: number;
    total_comments?: number;
    total_dislikes?: number;
    _id: string;
    /** Comment text */
    comment: string;
    /** act ID of the comment */
    act?: string;
    /** User */
    user: TUserSummary | IUserSummary | IUserProfile;
    created_by: string;
    updatedAt: string;
    createdAt: string;
    id: string;
    likes?: Array<CommentLike>;
    parent_comment?: string;
    replies?: Array<TComment>;
    project?: TProjectSummary;
    user_like?: CommentLike[];
    is_user_liked?: boolean;
};

export type TUserSummary = {
    role: string;
    audme_profile: string;
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    profile_picture: string;
    id: string;
    userName?: string;
};

export type CommentLike = {
    created_at: string;
    like_type: Likes;
    _id: string;
    user: string;
    created_by: string;
    comment: string;
};

export type TProjectSummary = {
    id: string;
    _id: string;
};

/** The below will be used for deleting, updating or fetching a doc */
export interface IGenericDocumentModifier {
    /** The Id of the documnet in question */
    documentId: string;
    /** Add an details to help woth modifiying document */
    payload?: any;

    additionalPayload?: any;
    /** Used to redirect when authentication fails */
    redirect?: string;
    morePayload?: any;
    fromCreate?: boolean;
    oldPosition?: any; // Added for awrd fix TODO: remove in the future
    /** The link to delete with */
    cache?: string;
}
export interface IEnableAudoLink {
    Instagram: boolean;
    YouTube: boolean;
    TikTok: boolean;
}
export interface IBuzzBridgeConfig {
    enableAutoLink: IEnableAudoLink;
    /** When true, Instagram comment automation features are enabled platform-wide. */
    instagram_comment_automation_enabled?: boolean;
    /**
     * When true, simple Instagram connect uses Instagram Login (instagram_business_basic).
     * Off until Meta approves — then enable in global config.
     */
    instagram_login_enabled?: boolean;
    campaign_platforms?: ICampaignPlatformConfig[];
    ugc_budget?: IUgcBudgetConfig;
    creator_payout?: ICreatorPayoutFeeConfig;
    /** Per-country review / payout countdown windows. */
    campaign_deadlines?: ICampaignDeadlinesConfig;
    /** Flat platform fee (USD cents) charged when adding creator seats. */
    seat_increase_fee_cents?: number;
}

export interface IInfluencerTier extends IInfluencerTierConfig {}

export interface IAdControl {
    skipped: number;
    ended: number;
    start: number;
}

interface ISalesFees {
    stripe_fees: number;
    stripe_fixed_fee: number;
    platform_fixed_fee: number;
    platform_fee: number;
}
export interface IGlobalConfiguration {
    audition_filters: AuditionFilters;
    talent_filter: TalentFilter;
    features: Features;
    ring: string;
    default_casting_limit: number;
    default_application_limit: number;
    act_limit: number;
    contest_submission_limit: number;
    client: string;
    _id: string;
    filters: Filters;
    permissions: any[];
    created_by: string;
    use_cache: boolean;
    cache_duration: number;
    media_control: IGlobalConfigurationMediaControl;
    /** When showing list of currencies, this option will be used */
    currency_options: string[];
    /** The currency that will be used across the app for setting price */
    default_currency: string;
    /** min ticket price */
    min_ticket_price: number; // In cents
    /**  The charge per ticket sold*/
    ticket_flat_fee: number; // In cents
    /** The percentage cost for each ticket sold in percent */
    ticket_additional_fee: number;
    max_ticket_price: number;
    buzzbridge: IBuzzBridgeConfig;
    buzzbridge_loyalty?: IBuzzbridgeLoyaltyConfig;
    ad_control?: IAdControl;
    min_submission_price?: number; // maps to min_fee
    submission_flat_fee?: number; // maps to thspian_fee
    added_percentage_per_submission?: number; // maps to added_percentage_per_submission
    max_submission_price?: number;
    campaign_max?: number; // maps to CAMPAIGN_MAX
    campaign_percentage?: number; // maps to PERCENTAGE
    campaign_flat_fee?: number; // maps to FLAT_FEE
    past_winner_playlist?: string;
    /** Minimum payout / withdrawal amount in USD cents (default 6000 = $60). */
    minimum_withdrawal_cents?: number;
    sales_fees?: ISalesFees;
    influencer_tiers?: IInfluencerTier[];
    two_fa_configuration?: {
        audme: boolean;
        email: boolean;
        sms: boolean;
        whatsapp: boolean;
    };
    content_scrub?: GlobalConfigurationContentScrubResponse;
}

export interface IGlobalConfigurationMediaControl {
    file_size_limit: number;
    mobile_size_limit?: number;
    image_size_limit?: number;
    pagination_count: number;
    supported_file_formats: SupportedFileFormat[];
    compressionQualityHigh: number;
    compressionQualityMid: number;
    compressionQualityLow: number;
}

export interface SupportedFileFormat {
    base: string;
    extension: string[];
    _id: string;
}

export interface AuditionFilters {
    rehearsal_date: string;
    required_media: string;
    age_range: string;
    filming_location: string;
    compensation: string;
    audition_type: string;
    required_skill: string;
    tags: string;
    employment_type: string;
}

export interface TalentFilter {
    location: string;
    age_range: string;
    height: string;
    skills: string;
    sex: string;
    hair_color: string;
    body_type: string;
    weight: string;
    language_spoken: string;
    special_skills: string;
}

export interface Features {
    account_features: AccountFeatures;
    voting: 'active' | 'inactive';
    movie_ticket: 'active' | 'inactive';
    audition: 'active' | 'inactive';
    acts: 'active' | 'inactive';
    ads: 'active' | 'inactive';
    contest_emails: 'active' | 'inactive';
    hls_play: 'active' | 'inactive';
    /** When active, search/discovery may use linked IG/TikTok avatars when profile_picture is empty. */
    influencer_platform_avatar_search?: 'active' | 'inactive';
    /** When active, creator subscription free trial is offered (Paystack / Flutterwave / Stripe). */
    creator_subscription_trial?: 'active' | 'inactive';
    /** When inactive, social manager is only visible to super-admins. */
    social_manager?: 'active' | 'inactive';
}

export interface AccountFeatures {
    movies: string;
    productions: string;
    contributions: string;
    profile: string;
    reviews: string;
    applications: string;
    settings: string;
    people: string;
    tickets: string;
    likes: string;
    boxofix_score: string;
    subscriptions: string;
}

export interface Filters {
    rehearsal_date: string;
    required_media: string;
    age_range: string;
    filming_location: string;
    compensation: string;
    audition_type: string;
}

export enum CONTEST_PHASE {
    AUDIENCE_VOTE = 'vote',
    IDEATION = 'ideation',
    JUDGING = 'judging',
    NONE = 'none',
    PITCH = 'pitch',
    PRODUCTION = 'production',
    SUBMISSION = 'submission',
    CLOSED = 'closed',
    EVENT = 'event',
}

export interface IAdvancedContest {
    _id: string;
    requirements: string[];
    contest_media: any[];
    voting_required: boolean;
    voting_type: string;
    date_start: string;
    expiration_date: string;
    contest_phases_enabled: boolean;
    premium: boolean;
    contest_phase: string;
    status: IContestStatus;
    title: string;
    description: string;
    created_by: string;
    contest_phase_dates: PhaseData[];
    feature_image: string;
    tag: string[];
    trending_marker: number;
    host: IHost;
    vote_stats: VoteStat[];
    total_vote_counted: number;
    submission_stats: SubmissionStat[];
    total_submission: number;
    total_votes: number;
    prizes: Prize[];
    partner: IPartner[];
    top_performers: ISubmission[];
    submission_trend: number[];
    vote_trend: number[];
    contest_id: string;
    submission_email_sent?: boolean;
    vote_email_sent?: boolean;
    winner_email_sent?: boolean;
    contest_open_email_sent?: boolean;
    awards?: IContestAward[];
    categories_enabled: boolean;
    categories: string[];
    total_registration_counted?: number;
    registration?: IAttendance[];
    playlist: string[];
    kind?: 'online' | 'physical';
}

export interface VoteStat {
    _id: string;
    number_of_votes: number;
    vote_rate: number;
}

export interface SubmissionStat {
    _id: string;
    number_of_submissions: number;
    submission_rate: number;
}

export interface Prize {
    _id: string;
    created_at: string;
    category_title: string;
    year_started: string;
    contest: string;
    created_by: string;
    sub_category?: 'male' | 'female';
    __v: number;
}

export enum ECONTEST_PHASE {
    PREPARATIION = 'preparation',
    SUBMISSION = 'submission',
    IDEATION = 'ideation',
    PITCH = 'pitch',
    PRODUCTION = 'production',
    JUDGING = 'judging',
    AUDIENCE_VOTE = 'vote',
    REGISTRATION = 'registration',
    VOTE = 'vote',
    NONE = 'none',
    CLOSED = 'closed',
}

export interface IReedemableCode {
    _id: string;
    used: boolean;
    code: string;
    expiration_date: string;
    created_by: string;
    contest?: string;
    event?: string;
    audition?: string;
    createdAt: string;
    updatedAt: string;
    used_on?: string;
    user: IUserSummary;
    active?: boolean;
    discount_structure?: 'one_time' | 'time_period';
}

export interface IContestAward {
    award_status: AwardStatus;
    created_at: string;
    createdAt: string;
    updatedAt: string;
    _id: string;
    award_year: string;
    person_awarded: IUserSummary;
    feature_image?: string;
    hash?: string;
    award_category: AwardCategory;
    award_project: IProjectSummary;
    contest: IContest;
    created_by: string;
    id: string;
    staff_pick?: boolean;
}

export type CreatorLocation = 'others' | 'submissions' | undefined;
export enum ECreatorLocation {
    OTHERS = 'others',
    SUBMISSIONS = 'submisisons',
}

export type TSelectionState = 'selected' | 'not_selected';
export interface IContestSubmissionsWithVotes {
    _id: string;
    trending_marker: number;
    judging_status: string;
    deleted: boolean;
    contest: string;
    project: IProject;
    created_by: string;
    createdAt: string;
    updatedAt: string;
    total_votes: number;
    total_average?: number;
    pre_selection_completed?: boolean;
    selection_state?: TSelectionState;
    selection_email_sent?: boolean;
}

export interface INewScoreCategory {
    _id: string;
    category_title: string;
    tag: string[];
    created_by: string;
    createdAt: string;
    updatedAt: string;
}

export interface INewScore {
    category: string;
    jury: string;
    score: number;
    submission: ISubmission | string;
    created_by: IUserSummary | string;
}

export type TSource = {
    src: string;
    /** "video/mp4" || "application/x-mpegURL" */
    type: string;
    /** "240" || "360" || "480" || "720" */
    res?: string;
    /** res with p e.g 240p */
    label?: string;

    default?: boolean;
};

/** Metadata VTT */
export type TTrack = {
    kind: string;
    src: string;
    srlang?: string;
    label?: string;
    default?: string;
};

export interface IUpNext {
    sources: TSource[];
    poster: string;
    title: string;
    duration: string;
    project: IProject;
}

export interface INuevoPlaylist {
    sources: TSource[];
    title: string;
    thumb: string;
    duration: string;
    url: string;
    track?: TTrack[];
    slideImage?: string;
}

export interface IGetPlaylistResponse {
    results: IThspianPlaylist;
    currentPage?: number;
    totalPages?: number;
    totalCount?: number;
    pageSize?: number;
    /** determines if the user is in edit mode */
    isEdit?: boolean;
    hasNextPage?: boolean;
}

export interface IUpdateThspianResponse extends IUpdatePlaylist {
    response: IThspianPlaylist;
}

export interface IThspianPlaylist {
    _id: string;
    name: string;
    description: string;
    projects: IProject[];
    created_by: string | IUserSummary;
    playlist_type: 'video' | 'picture' | 'document' | 'mixed' | 'audio';
    created_at: string;
    playlist_id: string;
    deleted: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface IThspianRenderedPlaylist {
    _id: string;
    name: string;
    description: string;
    projects: IProject[];
    created_by: string | IUserSummary;
    playlist_type: 'video' | 'picture' | 'document' | 'mixed' | 'audio';
    created_at: string;
    playlist_id: string;
    deleted: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface ICreatePlaylist {
    name: string;
    description?: string;
    playlist_type: 'video' | 'picture' | 'document' | 'mixed' | 'audio';
    projects: [IProject];
}

export interface IUpdatePlaylist {
    /** Id of the playlist */
    id: string;
    projects?: IProject[];
    position?: number;
    project?: string;
    /** This determinse if the entire  arrangement if reordered*/
    rearrange?: boolean;
    /** Include The ID of the project to remove */
    remove?: boolean;
    fullProject?: IProject;
}

export interface IThougth {
    _id: string;
    thougth: string;
    images: string[];
    videos: string[];
    files: string[];
    audios: string[];
    created_by: IUserSummary;
    view_count: number;
    total_reactions: number;
    total_likes: number;
    trending_marker?: number;
    total_anger: number;
    total_comments: number;
    total_love: number;
    total_share: number;
    deleted: boolean;
    status: string;
    createdAt: string;
    updatedAt: string;
    __v?: number;
    comments: any[];
    likes: any[];
    user_like: any[];
    is_user_liked: boolean;
    scheduled_published_date?: string;
    is_background_color?: boolean;
    background_color?: string;
    font_family?: string;
}

export interface IDiscount {
    discount_name: string;
    start_date: string;
    end_date: string;
    discount_percentage: number;
    _id: string;
}

/** Unparsed fee and no conversains */
export interface IRawFeeStructure {
    name?: string;
    description?: string;
    amount?: number;
    _id: string;
    fee: number;
    currency: string;
    fee_features: string[];
    fee_name: string;
    discount_message: string;
    original_price: number;
}

export interface FeeStructure {
    _id: string;
    fee: string;
    currency: Currency;
    fee_features: string[];
    fee_name: string;
    discounts: IDiscount[];
    discount_message: string;
    original_price: number;
    transaction_currency?: Currency;
    converted_currency?: string;
    bare_amount?: number;
    original_currency: Currency;
    rate?: number;
    defaulted_to_usd?: boolean;
    formatted_for_display?: string;
    formatted_fee?: string;
    convertedAmount?: string;
    toSymbol?: string;
    original_amount?: number;
    converted_amount_no_symbol?: string;
    max_ticket_per_person?: number;
    available_qty?: number;
    total_sold?: number;
}

export type TFAQ = {
    question: string;
    answer: string;
};

export type IEventKind =
    | 'watch party'
    | 'networking'
    | 'conference'
    | 'film festival'
    | 'music festival'
    | 'meet up'
    | 'dance party'
    | 'theme party'
    | 'creator fest';

export interface IEvent {
    _id: string;
    event_id?: string;
    name: string;
    description?: string;
    kind?: IEventKind;
    timezone?: string;
    refund_policy?: string;
    max_ticket_purchasable_per_person?: number;
    total_tickets?: number;
    age_restriction?: number;
    dress_code?: string;
    event_date_breakdown?: Array<{
        start_date: Date;
        end_date: Date;
        detail?: string;
        location?: {
            type: string;
            coordinates: number[];
            address: string;
            description: string;
            short_name: string;
        };
    }>;
    tags: string[];
    event_start_date: string;
    event_end_date: string;
    linked_event?: IEvent | IContest;
    linked_event_type?: 'Festival' | 'Contest' | 'Event';
    location: Location;
    with_fee?: boolean;
    fee_structure?: FeeStructure[];
    created_by: IUserSummary;
    status?: 'draft' | 'published';
    deleted?: boolean;
    createdAt?: string;
    updatedAt?: string;
    feature_image?: string;
    slug: string;
    requirements?: string[];
    with_registration?: boolean;
    about_host?: string;
    host_contact?: string;
    scheduled_published_date?: string;
    is_background_color?: boolean;
    background_color?: string;
    font_family?: string;
    has_user_registered?: boolean;
    user_registration?: string[];
    has_user_bookmarked?: boolean;
    user_bookmark?: string[];
    total_attendance?: number;
    total_interest?: number;
    prev_feature_image?: string;
    faq?: TFAQ[];
    state: IEventState;
    event_video?: IProject;
    do_not_convert_currency?: boolean;
    partners?: IPartner[];
    wheel_chair_accessible?: boolean;
    hide_location_until_ticket_purchase?: boolean;
    just_rsvp_required?: boolean;
    seated_event?: boolean;
    organizer?: IUserSummary;
}

export type IEventState = 'active' | 'cancelled' | 'postponed-indefinitely' | 'postponed';

export type TRefundPolicy = 'non-refundable' | 'refund up to 7 days before the event' | 'refundable at anytime';

export interface ILocation {
    address: string;
    description: string;
    coordinates: number[];
    type: string;
    short_name: string;
}

export interface IEventFAQ {
    question: string;
    answer: string;
}

export interface ICastingDirector {
    _id: string;
    location: ILocation;
    production_type: string[];
    gallery: string[];
    demo_reel: string[];
    status: string;
    created_at: string;
    verified: boolean;
    company_name: string;
    created_by: string;
    user: IUserSummary;
    createdAt: string;
    updatedAt: string;
    __v: number;
    about: string;
    profile_picture: string;
    number_of_reviews: number;
    rating: number;
    number_of_jobs: number;
}

export interface IDiscount {
    discount_name: string;
    start_date: string;
    end_date: string;
    discount_percentage: number;
}
export interface ITicket {
    fee: number;
    fee_name: string;
    currency: 'USD' | 'NGN' | 'EUR' | 'GBP';
    fee_features: string[];
    discounts: IDiscount[];
    /** This should not be sent to the server */
    displayCurrencyValue?: string;
}

// TypeScript Interface
export type LocationOption = {
    label: string;
    value: {
        coordinates: [number, number];
        address: string;
        description: string;
    };
};

export type Currency = 'USD' | 'NGN' | 'EUR' | 'GBP';
type Expertise = 'beginner' | 'mid-level' | 'expert';
type RoleType = 'technical' | 'cast' | 'crew';

export interface IRole {
    id: string;
    _id: string;
    character: string;
    age_range: string;
    required_media: string[];
    min_age: number;
    max_age: number;
    created_at?: string;
    createdAt?: string;
    gender: string;
    description: string;
    audition: IAudition;
    created_by: string;
    submissions: Array<IApplication>;
    compensation: string | number;
    currency?: Currency;
    expertise?: Expertise;
    role_type: RoleType | FormOption | string | undefined;
    is_paid?: 'paid' | 'unpaid' | 'tbd';
    employment_type?: string;
    requires_subscription?: boolean;
    work_requirement?: string[];
    nudity_or_kissing?: boolean;
    converted_compensation?: {
        amount: number;
        formatted_compensation: string;
        original_currency: string;
        rate: number;
    };
}

export interface IRoleCreator extends Partial<IRole> {}
export type PrizeD = {
    prize: string;
    local_identifier?: string;
    created_at?: string;
    _id?: string;
    category_title: string;
    sub_category?: 'male' | 'female';
    additional_prize?: string[];
    year_started?: string;
    contest?: string;
    created_by?: string;
    id?: string;
    currency?: {
        label: string;
        symbol: string;
    } | null;
};
export type TSkill = {
    skill: string;
    expertise: 'beginner' | 'mid-level' | 'expert';
    years_of_experience: number;
    description: string;
};

export type TLink = {
    link: string;
    description: string;
};

export type TProfileType = 'voiceover' | 'technical' | 'performer' | 'artist' | 'content_creator';

/**
 * 
bio
: 
"When we see it\n"
engagement
: 
2
followers
: 
400000
following
: 
500000
industry
: 
["Fashion & Beauty", "Finance & Investment"]
platform
: 
"instagram"
username
: 
"linterma"
_id
: 
"687548007a1b6706622a497d"
 */

export type TPlatform = 'instagram' | 'twitter' | 'facebook' | 'thspian' | 'youtube' | 'tiktok';
export interface IProfileSocials {
    bio: string;
    engagement: number;
    followers: number;
    following: number;
    platform: TPlatform;
    username: string;
}
export interface IWorkProfile {
    about?: string;
    socials?: IProfileSocials[];
    title?: string;
    profile_picture?: string;
    ethnicity?: TEthinities[];
    feature_picture?: string;
    age_from?: number;
    age_to?: number;
    audio_reel?: ISound[];
    birth_data?: string;
    body_type?: 'athletic' | 'thin' | 'curvy' | 'average' | 'any';
    demo_reel?: IProject[];
    expertise?: string;
    gender?: string;
    sex?: string;
    hair_color?: string;
    height?: number;
    language_spoken?: TLanguage[];
    links?: TLink[];
    location: Location;
    number_of_reviews?: number;
    other_skills?: TSkill[];
    photos: string[];
    rating?: number;
    skills?: TSkill[];
    special_skills?: string[];
    stage_name?: string;
    user: IUserSummary;
    weight?: number;
    profile_type: TProfileType;
    _id: string;
    minDistance?: number;
    feature_audio?: string;
    feature_video?: string;
    user_saved_object?: any[];
    has_user_saved?: boolean;
    other_profiles?: IWorkProfile[];
    created_by?: IUserSummary;
    featured?: boolean;
    credits?: IPastWork[];
    short_url?: string;
}
export interface ITaentReviewResponse {
    status: string;
    results: number;
    total: number;
    totalPages: number;
    currentPage: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
    nextPage: any;
    prevPage: any;
    data: ITalentReview[];
}

export interface ITalentReview {
    _id: string;
    created_at: string;
    rating: number;
    review_subject: string;
    review_text: string;
    talent: IWorkProfile;
    user: IUserSummary;
    created_by: IUserSummary;
    total_likes?: number;
    total_dislikes?: number;
    /** will contain the like object and almost alway sjust one object */
    dislike_object?: ILike[];
    /** will contain the like object and almost alway sjust one object */
    like_object?: ILike[];
    user_has_liked?: boolean;
    user_has_disliked?: boolean;
    edited?: boolean;
    edited_at?: string;
}

export interface IPaginator {
    /** Informs on the current page we are viewing */
    currentPage: number;
    /** Also known as limit, how many items are on this page */
    pageSize: number;
    /** Total results for this current page */
    results: number;
    /** Total number of items without pagination */
    totalCount: number;
    /** Total pages */
    totalPages: number;
}

export interface IGetAllResponse<T> {
    /** Returns success or fail */
    status: string;
    /** Total number of items not paginated */
    totalCount: number;
    /** The current page */
    currentPage: number;
    /** The limit: 20 */
    pageSize: number;
    /** The number of paginated items */
    results: number;
    /** Total number of pages */
    totalPages: number;
    data: { data: T[] };
}

/**System Forms */
export enum FORM_IDENTIFIER_ENUM {
    ADD_ROLE = 'add_role',
    ADD_AUDITION_DETAILS = 'add_audition_details',
    CREATE_PRODUCTION_COMPANY = 'create_production_company',
    ADD_AUDITION_LOCATIONS = 'add_audition_locations',
    ADD_AUDITION_DATES = 'add_audition_dates',
    ADD_AUDITION_TAGS = 'add_audition_tags',
    ADD_EVENT_DETAILS = 'add_event_details',
    ADD_HOST_DETAILS = 'add_host_details',
    ADD_EVENT_TAGS = 'add_event_tags',
    ADD_ADDITIONAL_EVENT_DETAILS = 'add_additional_event_details',
    CREATE_EVENT_TICKET = 'create_event_ticket',
    CREATE_EVENT_DISCOUNT = 'create_event_discount',
    CREATE_EVENT_FAQ = 'create_event_faq',
    CREATE_CAMPAIGN = 'create_campaign',
    CREATE_WORK_PROFILE = 'create_work_profile',
    CREATE_NEW_SOUND = 'create_new_sound',
}

export type TSaleCategory =
    | 'tickets'
    | 'submissions'
    | 'donations'
    | 'funding'
    | 'tokens'
    | 'sandbox_course'
    | 'sandbox_office_hours';
type TEventType = 'Festival' | 'Contest' | 'Event' | 'Project' | 'SandboxCourse' | 'CalendarBooking' | 'Booking';

/** This object is needed by the backend to properly
 * record this sale event and MUST be provided
 */
export interface ISaleObject {
    category: TSaleCategory;
    /** Can be a type of event ticket, contest sale or  donation*/
    item: any[];
    /** The currency that the transaction was processed with */
    transaction_currency: string;
    /** Must be linked to an event, contest, project or festival */
    linked_event: string;
    linked_event_type: TEventType;
    email_used?: string;
    max_items?: number;
    exchange_rate?: number;
}

export interface ITicket {
    _id: string;
    sale?: ISale;
    event: IEvent | IContest | IProject;
    event_type: string;
    seat_info: string;
    total_seats: number;
    checked_in: boolean;
    email_sent: boolean;
    recipient_email: string;
    recipient_name: string;
    event_details: EventDetails;
    deleted: boolean;
    ticket_id: string;
    createdAt: string;
    updatedAt: string;
    qr_code: string;
    __v: number;
    email_sent_at: string;
}

export interface ISale {
    _id: string;
    items: Item[];
    price: number;
    linked_event: string;
    invoice: Invoice;
}

export interface Item {
    id: string;
    title: string;
    description: string;
    original_price: number;
    price: number;
    currency: string;
    quantity: number;
    image: string;
    details: Detail[];
}

export interface Detail {
    label: string;
    value: string;
}

export interface Invoice {
    id: string;
    object: string;
    amount: number;
    amount_captured: number;
    amount_refunded: number;
    application: any;
    application_fee: any;
    application_fee_amount: any;
    balance_transaction: string;
    billing_details: BillingDetails;
    calculated_statement_descriptor: string;
    captured: boolean;
    created: number;
    currency: string;
    customer: string;
    description: any;
    destination: any;
    dispute: any;
    disputed: boolean;
    failure_balance_transaction: any;
    failure_code: any;
    failure_message: any;
    fraud_details: FraudDetails;
    invoice: any;
    livemode: boolean;
    metadata: Metadata;
    on_behalf_of: any;
    order: any;
    outcome: Outcome;
    paid: boolean;
    payment_intent: string;
    payment_method: string;
    payment_method_details: PaymentMethodDetails;
    radar_options: RadarOptions;
    receipt_email: any;
    receipt_number: any;
    receipt_url: string;
    refunded: boolean;
    refunds: Refunds;
    review: any;
    shipping: any;
    source: any;
    source_transfer: any;
    statement_descriptor: any;
    statement_descriptor_suffix: any;
    status: string;
    transfer_data: any;
    transfer_group: any;
}

export interface BillingDetails {
    address: Address;
    email: any;
    name: string;
    phone: any;
    tax_id: any;
}

export interface Address {
    city: any;
    country: string;
    line1: any;
    line2: any;
    postal_code: any;
    state: any;
}

export interface FraudDetails {}

export interface Metadata {
    billing_address: string;
    sale_id: string;
    name: string;
}

export interface Outcome {
    advice_code: any;
    network_advice_code: any;
    network_decline_code: any;
    network_status: string;
    reason: any;
    risk_level: string;
    risk_score: number;
    seller_message: string;
    type: string;
}

export interface PaymentMethodDetails {
    card: Card;
    type: string;
}

export interface Card {
    amount_authorized: number;
    authorization_code: any;
    brand: string;
    checks: Checks;
    country: string;
    exp_month: number;
    exp_year: number;
    extended_authorization: ExtendedAuthorization;
    fingerprint: string;
    funding: string;
    incremental_authorization: IncrementalAuthorization;
    installments: any;
    last4: string;
    mandate: any;
    multicapture: Multicapture;
    network: string;
    network_token: NetworkToken;
    network_transaction_id: string;
    overcapture: Overcapture;
    regulated_status: string;
    three_d_secure: any;
    wallet: any;
}

export interface Checks {
    address_line1_check: any;
    address_postal_code_check: any;
    cvc_check: any;
}

export interface ExtendedAuthorization {
    status: string;
}

export interface IncrementalAuthorization {
    status: string;
}

export interface Multicapture {
    status: string;
}

export interface NetworkToken {
    used: boolean;
}

export interface Overcapture {
    maximum_amount_capturable: number;
    status: string;
}

export interface RadarOptions {}

export interface Refunds {
    object: string;
    data: any[];
    has_more: boolean;
    total_count: number;
    url: string;
}

export interface Event {
    _id: string;
    name: string;
    location: Location;
    fee_structure: FeeStructure[];
    created_by: CreatedBy;
}

export interface CreatedBy {
    _id: string;
    role: string;
    firstName: string;
    lastName: string;
    profile_picture: string;
    userName: string;
    audme_profile: string;
    permissions: string[];
    user_type: string[];
    visible: boolean;
    identified: boolean;
    verified: boolean;
    feature_image: string;
    interest: string[];
    profile_picture_hash: string;
    feature_image_hash: string;
    social_thumbnail: string;
}

export interface EventDetails {
    name: string;
    date: string;
    time: string;
    location: string;
}

// Currency symbols mapping
export const CURRENCY_SYMBOLS: Record<Currency, string> = {
    USD: '$',
    GBP: '£',
    EUR: '€',
    NGN: '₦',
};

// Default currency
export const DEFAULT_CURRENCY: Currency = 'USD';

export interface IPastWork {
    _id: string;
    profile?: string;
    title: string;
    type?: string;
    role?: string[];
    awards?: any[];
    created_by?: string;
    deleted?: boolean;
    link?: string;
    company?: ICompany;
    description?: string;
    year: string;
}
